"use client";

import {
	RiCalendarLine,
	RiGovernmentLine,
	RiMapPinLine,
	RiTimeLine,
} from "@remixicon/react";
import {
	formatShortDateWithPreferences,
	formatShowTimeRange,
	getShowDisplayParts,
	type ProcessedShow,
} from "@workspace/shared-utils";
import type { ProcessedSet } from "@workspace/types/src/entities/festival";
import { Card } from "@workspace/ui/components/card";
import { Text } from "@workspace/ui/components/custom/text";
import { memo } from "react";
import Link from "next/link";
import { Facepile } from "@/components/shared/facepile";
import { FacepileContainer } from "@/components/shared/facepile-skeleton";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import { cn } from "@/lib/utils";
import { ShowAttendanceButton } from "../buttons/show-attendance-button";
import { ShowPhoto } from "../shared/show-photo";

interface FollowerInteraction {
	id: string;
	username: string | null;
	display_name: string;
	manual_avatar_url?: string | null;
	google_avatar_url?: string | null;
	// Show review data
	rating?: number;
	loved?: boolean;
	must_see?: boolean;
	want_to_see?: boolean;
	saw_live?: boolean;
	saw_stream?: boolean;
	content?: string;
	// Show attendance data
	show_status?: "want_to_go" | "going" | null;
}

interface Venue {
	id: string;
	name: string;
	slug: string;
	address?: string;
	city: string;
	state?: string;
	country: string;
	capacity?: number;
	venue_type?: string;
}

interface Show {
	id: string;
	title: string;
	artist_name: string;
	date: string;
	time_start?: string;
	time_end?: string;
	location: string;
	age_limit?: string;
	poster_url?: string;
	slug: string;
	created_at: string;
	updated_at: string;
	artist_id?: string;
	venue_id: string;
	venue: Venue;
	website_url?: string;
	youtube_url?: string;
	soundcloud_url?: string;
	production_company?: string;
	presents_name?: string | null;
	is_collaborative?: boolean;
	collaboration_format?: "×" | "x" | "B2B" | "+" | "&" | "vs." | ",";
	collaboration_alias?: string | null;
	show_artists?: Array<{
		id: string;
		position: number;
		time_start?: string;
		time_end?: string;
		is_headliner: boolean;
		role: "headliner" | "collaborator" | "supporting_artist";
		artist: {
			id: string;
			name: string;
			image_url?: string | null;
			[key: string]: unknown;
		};
	}>;
}

interface ShowCardProps {
	show: Show;
	priority?: boolean;
	fetchPriority?: "high" | "low" | "auto";
	loading?: "eager" | "lazy";
	className?: string;
	interactions?: FollowerInteraction[];
	isFacepileLoading?: boolean;
}

/**
 * Get show image URL with fallback logic
 * Priority: poster_url > headliner artist image > placeholder
 */
function getShowImageUrl(show: Show): string | null {
	if (show.poster_url) {
		return show.poster_url;
	}

	const headliner = show.show_artists?.find((sa) => sa.is_headliner);
	if (headliner?.artist?.image_url) {
		return headliner.artist.image_url;
	}

	return null; // ShowPhoto component will display placeholder icon
}

export const ShowDiscoveryCard = memo<ShowCardProps>(
	({
		show,
		priority = false,
		fetchPriority = "auto",
		loading = "lazy",
		className,
		interactions = [],
		isFacepileLoading = false,
	}) => {
		const userPreferences = useUserPreferences();

		// Get display parts for collaboration handling
		const displayParts = getShowDisplayParts(show as unknown as ProcessedShow);

		// Get image URL with fallback logic
		const imageUrl = getShowImageUrl(show);

		// Check if show is in the past
		const showIsPast = (() => {
			// Parse date without timezone conversion
			const parts = show.date.split("-");
			const year = Number(parts[0]);
			const month = Number(parts[1]);
			const day = Number(parts[2]);
			const showDate = new Date(year, month - 1, day);
			showDate.setHours(23, 59, 59, 999);
			return showDate < new Date();
		})();

		return (
			<Link href={`/shows/${show.slug}`} className={cn("group block", className)}>
				<Card
					className={cn(
						"overflow-hidden cursor-pointer",
						"hover:bg-muted/30 dark:hover:bg-muted/10",
						"active:bg-muted/50 dark:active:bg-muted/20",
						"active:scale-[0.99]",
						"transition-all duration-150",
					)}
					style={{ WebkitTapHighlightColor: "transparent" }}
				>
					<div className="flex gap-3 p-3">
						{/* Show image - 64x64px square thumbnail */}
						<div className="relative flex-shrink-0 overflow-hidden rounded-lg">
							<div className="h-16 w-16">
								<ShowPhoto
									src={imageUrl}
									alt={`${show.artist_name} at ${show.venue?.name || show.location || "venue"}`}
									priority={priority}
									fetchPriority={fetchPriority}
									loading={loading}
										rounded="lg"
								/>
							</div>
						</div>

						{/* Show info */}
						<div className="min-w-0 flex-1">
							{/* Main content area */}
							<div className="flex items-start justify-between gap-2">
								<div className="min-w-0 flex-1">
									{/* Artist name */}
									<h3 className="line-clamp-1 font-semibold leading-tight transition-colors group-hover:text-primary">
										{displayParts.parts && displayParts.parts.length > 0 ? (
											displayParts.parts.map((part, index) => {
												const key = `${show.id}-part-${index}`;
												if (part.type === "artist") {
													return <span key={key}>{part.text}</span>;
												} else {
													return (
														<span
															key={key}
															className="ml-1 font-normal text-muted-foreground/50"
														>
															{part.text}
														</span>
													);
												}
											})
										) : (
											<>
												{displayParts.mainText}
												{displayParts.connector && (
													<span className="ml-1 font-normal text-muted-foreground/50">
														{displayParts.connector}
													</span>
												)}
												{displayParts.secondaryText && (
													<>
														{displayParts.connector && " "}
														{displayParts.secondaryText}
													</>
												)}
											</>
										)}
									</h3>

									{/* Venue */}
									<div className="flex items-center gap-1 text-muted-foreground text-xs">
										<RiGovernmentLine className="h-3 w-3 flex-shrink-0" />
										<Text
											variant="body-sm"
											className="line-clamp-1 text-muted-foreground leading-tight"
										>
											{show.venue?.name || "Venue TBD"}
										</Text>
									</div>

									{/* Location - City, State */}
									{show.venue && (show.venue.city || show.venue.state || show.venue.country) && (
										<div className="flex items-center gap-1 text-muted-foreground text-xs">
											<RiMapPinLine className="h-3 w-3 flex-shrink-0" />
											<Text
												variant="body-sm"
												className="line-clamp-1 text-muted-foreground leading-tight"
											>
												{[show.venue.city, show.venue.state || show.venue.country]
													.filter(Boolean)
													.join(", ")}
											</Text>
										</div>
									)}

									{/* Date and Time */}
									<div className="flex flex-col gap-0.5 text-muted-foreground text-xs">
										<div className="flex items-center gap-1">
											<RiCalendarLine className="h-3 w-3 flex-shrink-0" />
											<Text
												variant="body-sm"
												className="text-muted-foreground leading-tight"
											>
												{formatShortDateWithPreferences(show.date, userPreferences)}
											</Text>
										</div>
										{/* Time row */}
										<div className="flex items-center gap-1">
											<RiTimeLine className="h-3 w-3 flex-shrink-0" />
											<Text
												variant="body-sm"
												className="text-muted-foreground leading-tight"
											>
												{formatShowTimeRange(
													show.time_start,
													show.time_end,
													userPreferences,
												)}
											</Text>
										</div>
									</div>
								</div>

								{/* Right column: button (top) + facepile (bottom) */}
								<div
									className="relative z-10 flex flex-col items-end justify-between self-stretch"
									onClick={(e) => e.stopPropagation()}
								>
									<ShowAttendanceButton
										showId={show.id}
										showIsPast={showIsPast}
										size="sm"
									/>

									{/* Facepile at bottom right */}
									<FacepileContainer hasData={interactions.length > 0}>
										<Facepile
											interactions={interactions}
											maxAvatars={3}
											size="sm"
											allowHoverInModal={false}
											disableLinks={true}
											set={
												{
													// Provide minimal set data for status badge logic
													id: show.id,
													start_time: `${show.date}T${show.time_start || "00:00:00"}`,
													end_time: show.time_end
														? `${show.date}T${show.time_end}`
														: `${show.date}T23:59:59`,
													artist: null,
													stage: null,
													alias_name: null,
													is_group_alias: false,
													presents_name: null,
													friends_set: false,
													is_collaborative: false,
													display_format: undefined,
													is_surprise: false,
													placeholder_name: null,
													special_guests: null,
													day: (() => {
														const parts = show.date.split("-");
														const year = Number(parts[0]);
														const month = Number(parts[1]);
														const day = Number(parts[2]);
														const date = new Date(year, month - 1, day);
														return date.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
													})(),
													startHour: 0,
													durationHours: 0,
													time: "",
													youtube_url: null,
												} as ProcessedSet
											}
										/>
									</FacepileContainer>
								</div>
							</div>
						</div>
					</div>
				</Card>
			</Link>
		);
	},
);

ShowDiscoveryCard.displayName = "ShowDiscoveryCard";
